import Vue from 'vue'
import Router from 'vue-router'

import store from '@/store/index.js'
import { trackPageArrivals } from '@/utils/pageArrival.js'
import Home from '@/views/Home.vue'
import FAQ from '@/views/FAQ.vue'
import TrackSearch from '@/views/TrackSearch.vue'
import AiTrackSearch from '@/views/AiTrackSearch.vue'
import SfxSearch from '@/views/SfxSearch.vue'
import ArtistDetail from '@/views/ArtistDetail.vue'
import TrackDetail from '@/views/TrackDetail.vue'
import ProfileParent from '@/views/ProfileParent.vue'
import ProfileUpdate from '@/views/ProfileUpdate.vue'
import CardDetailsUpdate from '@/views/CardDetailsUpdate.vue'
import CollectionDetail from '@/views/CollectionDetail.vue'
import SharedCollectionDetail from '@/views/SharedCollectionDetail.vue'
import SharedPlaylistDetail from '@/views/SharedPlaylistDetail.vue'
import CollectionList from '@/views/CollectionList.vue'
import LicenseList from '@/views/LicenseList.vue'
import InvoiceList from '@/views/InvoiceList.vue'
import FavouriteTracksList from '@/views/FavouriteTracksList.vue'
import PlaylistList from '@/views/PlaylistList.vue'
import PlaylistDetail from '@/views/PlaylistDetail.vue'
import Pricing from '@/views/Pricing.vue'
import Cart from '@/views/Cart.vue'
import CartCheckout from '@/views/CartCheckout.vue'
import CustomOrderCart from '@/views/CustomOrderCart.vue'
import CustomOrderCartCheckout from '@/views/CustomOrderCartCheckout.vue'
import NotFound from '@/views/NotFound.vue'
import PasswordResetConfirm from '@/views/PasswordResetConfirm.vue'
import PrivacyPolicy from '@/views/PrivacyPolicy.vue'
import Subscription from '@/views/Subscription.vue'
import SubscriptionCheckout from '@/views/SubscriptionCheckout.vue'
import UserTerms from '@/views/UserTerms.vue'
import ArtistSubmissions from '@/views/ArtistSubmissions.vue'
import LandingPageHandPicked from '@/views/LandingPageHandPicked.vue'
import LandingPageVideoEditor from '@/views/LandingPageVideoEditor.vue'
import KeywordLandingPage from '@/views/KeywordLandingPage.vue'
import AuthPages from '@/views/AuthPages.vue'
import SubscribePlan from '@/views/SubscribePlan.vue'
import SubscribePayment from '@/views/SubscribePayment.vue'

import multiguard from 'vue-router-multiguard'
import { AUTH_PAGES_SWITCH } from '@/utils/authPages.js'
import { SUBSCRIBE_FLOW_SWITCH } from '@/utils/subscribeFlow.js'

Vue.use(Router)

const ensureLoggedIn = (to, from, next) => {
  if (store.getters['auth/isLoggedIn']) {
    next()
    return
  }
  // On a direct load the switches may not have loaded yet; wait so the right
  // log-in (page or modal) is used. If waffle can't load, use the modal.
  store.dispatch('waffle/ensureLoaded')
    .catch(() => {})
    .then(() => {
      if (store.getters['waffle/isFeatureEnabled'](AUTH_PAGES_SWITCH)) {
        next({ name: 'login', query: { next: to.fullPath } })
        return
      }
      store.dispatch('auth/setRedirectTo', to.fullPath)
      store.dispatch('auth/showLoginModal')
      // On a direct load or refresh there's no page to stay on, so show the home
      // page behind the login modal rather than a blank one.
      if (from.matched.length === 0) {
        next({ name: 'home' })
      } else {
        next(false)
      }
    })
}

const ensureLoggedOut = (to, from, next) => {
  if (!store.getters['auth/isLoggedIn']) {
    next()
  } else {
    next({ name: 'profile-edit' })
  }
}

// For pages behind a waffle switch: a 404 while it's off.
const ensureSwitchOn = (name) => (to, from, next) => {
  store.dispatch('waffle/ensureLoaded')
    .catch(() => {})
    .then(() => {
      if (store.getters['waffle/isFeatureEnabled'](name)) {
        next()
      } else {
        next({ name: 'not-found' })
      }
    })
}

const ensureCartHasItems = (to, from, next) => {
  if (store.getters['cart/cartCount'] > 0) {
    next()
  } else {
    next({ name: 'cart' })
  }
}

const ensureSelectedPlan = (to, from, next) => {
  if (store.getters['subscriptions/selectedPlan']) {
    next()
  } else {
    next({ name: 'pricing' })
  }
}

const validatePasswordResetToken = (to, from, next) => {
  store.dispatch('auth/passwordResetValidate', to.params.token)
    .then(() => {
      next()
    })
    .catch(() => {
      next({ name: 'not-found' })
    })
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      children: [
        {
          path: 'activate-account/:partialId',
          component: Home,
          props: true,
          meta: {
            showActivateAccountModal: true
          }
        }
      ]
    },
    // Landing pages
    {
      path: '/video-editors-first100',
      name: 'landing-page-video-editor',
      component: LandingPageVideoEditor
    },
    {
      path: '/start',
      name: 'landing-page-hand-picked',
      component: LandingPageHandPicked
    },
    {
      // Keyword landing pages. Prerendered HTML is served by the static host
      // when present (dist/browse/<slug>/index.html); for non-prerendered
      // slugs the SPA hydrates via the retrieve API. Namespaced under
      // /browse/ so it can't shadow other top-level routes or change 404
      // behaviour.
      path: '/browse/:slug',
      name: 'keyword-landing-page',
      component: KeywordLandingPage,
      props: true
    },
    // End: Landing pages
    {
      path: '/pricing',
      name: 'pricing',
      component: Pricing
    },
    {
      path: '/privacy',
      name: 'privacy-policy',
      component: PrivacyPolicy
    },
    {
      path: '/terms',
      name: 'user-terms',
      component: UserTerms
    },
    {
      path: '/faq',
      name: 'faq',
      component: FAQ
    },
    {
      path: '/artist-submissions',
      name: 'artist-submissions',
      component: ArtistSubmissions
    },
    {
      path: '/search',
      redirect: { name: 'track-search' }
    },
    {
      path: '/tracks',
      name: 'track-search',
      component: TrackSearch
    },
    {
      path: '/ai-search',
      name: 'ai-track-search',
      component: AiTrackSearch
    },
    {
      path: '/sfx',
      name: 'sfx-search',
      component: SfxSearch
    },
    {
      path: '/artist/:slug/:artistId',
      name: 'artist-detail',
      component: ArtistDetail,
      props: true
    },
    {
      path: '/track/:artistSlug/:slug/:trackId',
      name: 'track-detail',
      component: TrackDetail,
      props: true
    },
    {
      path: '/playlists',
      name: 'playlist-list',
      component: PlaylistList
    },
    {
      path: '/playlists/:privatePlaylistId/private',
      name: 'shared-playlist-detail',
      component: SharedPlaylistDetail,
      props: true
    },
    {
      path: '/playlists/:slug/:playlistId',
      name: 'playlist-detail',
      component: PlaylistDetail,
      props: true
    },
    {
      path: '/collection/:privateCollectionId/private',
      name: 'shared-collection-detail',
      component: SharedCollectionDetail,
      props: true
    },
    {
      path: '/cart',
      name: 'cart',
      component: Cart
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: CartCheckout,
      beforeEnter: multiguard([ensureLoggedIn, ensureCartHasItems])
    },
    {
      path: '/subscription-checkout',
      name: 'subscription-checkout',
      component: SubscriptionCheckout,
      beforeEnter: multiguard([ensureLoggedIn, ensureSelectedPlan])
    },
    {
      path: '/custom-order/:orderId/view-cart/',
      name: 'custom-order-cart',
      component: CustomOrderCart,
      props: true,
      beforeEnter: ensureLoggedIn
    },
    {
      path: '/custom-order/:orderId/checkout/',
      name: 'custom-order-checkout',
      component: CustomOrderCartCheckout,
      props: true,
      beforeEnter: ensureLoggedIn
    },
    {
      path: '/subscribe/plan',
      name: 'subscribe-plan',
      component: SubscribePlan,
      beforeEnter: multiguard([ensureSwitchOn(SUBSCRIBE_FLOW_SWITCH), ensureLoggedIn]),
      meta: {
        hideHeader: true,
        disableThePlayer: true
      }
    },
    {
      path: '/subscribe/payment',
      name: 'subscribe-payment',
      component: SubscribePayment,
      beforeEnter: multiguard([ensureSwitchOn(SUBSCRIBE_FLOW_SWITCH), ensureLoggedIn]),
      meta: {
        hideHeader: true,
        disableThePlayer: true
      }
    },
    {
      // Gated by the clean_signup_login_pages waffle switch in AuthPages.
      path: '/signup',
      name: 'create-account',
      component: AuthPages,
      beforeEnter: ensureLoggedOut,
      meta: {
        hideHeader: true,
        disableThePlayer: true,
        // Shared with login so App keeps the page mounted between the two.
        viewKey: 'auth-pages'
      }
    },
    {
      // Gated by the clean_signup_login_pages waffle switch in AuthPages.
      path: '/login',
      name: 'login',
      component: AuthPages,
      beforeEnter: ensureLoggedOut,
      meta: {
        hideHeader: true,
        disableThePlayer: true,
        viewKey: 'auth-pages'
      }
    },
    {
      path: '/password-reset/confirm/:token',
      name: 'password-reset-confirm',
      component: PasswordResetConfirm,
      props: true,
      beforeEnter: multiguard([ensureLoggedOut, validatePasswordResetToken])
    },
    {
      path: '/profile',
      component: ProfileParent,
      beforeEnter: ensureLoggedIn,
      children: [
        {
          path: '',
          redirect: { name: 'profile-edit' }
        },
        {
          path: 'edit',
          name: 'profile-edit',
          component: ProfileUpdate,
          beforeEnter: ensureLoggedIn
        },
        {
          path: 'update-card-details',
          name: 'card-details-update',
          component: CardDetailsUpdate,
          beforeEnter: ensureLoggedIn
        },
        {
          path: 'licenses',
          name: 'license-list',
          component: LicenseList,
          props: true,
          beforeEnter: ensureLoggedIn
        },
        {
          path: 'collections',
          name: 'collection-list',
          component: CollectionList,
          beforeEnter: ensureLoggedIn
        },
        {
          path: 'collections/:collectionId',
          name: 'collection-detail',
          component: CollectionDetail,
          props: true,
          beforeEnter: ensureLoggedIn
        },
        {
          path: 'favourites',
          name: 'favourite-tracks',
          component: FavouriteTracksList,
          beforeEnter: ensureLoggedIn
        },
        {
          path: 'subscription',
          name: 'subscription',
          component: Subscription,
          props: true,
          beforeEnter: ensureLoggedIn
        },
        {
          path: 'invoices',
          name: 'invoice-list',
          component: InvoiceList,
          beforeEnter: ensureLoggedIn
        }
      ]
    },
    {
      path: '/404',
      name: 'not-found',
      component: NotFound
    },
    {
      path: '*',
      redirect: { name: 'not-found' }
    }
  ]
})

// Whether each page was opened afresh or shown again (Reload, Back, Forward),
// for the search pages' analytics.
trackPageArrivals(router)

router.afterEach((to, from) => {
  if (window.analytics) {
    window.analytics.page(to.name || '', {
      path: to.fullPath,
      referrer: from.fullPath
    })
  }
})

export default router
